<template>
  <div class="space-y-6">
    <div>
      <div class="text-sm font-semibold text-slate-700">Gate operations</div>
      <h2 class="mt-1 text-2xl font-bold tracking-tight text-slate-900">Entry / Exit</h2>
    </div>

    <div class="grid gap-6 lg:grid-cols-2">
      <section class="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
        <div class="text-sm font-bold text-slate-900">Register entry</div>
        <div class="mt-1 text-xs text-slate-500">Enter plate number. Permanent status is checked automatically.</div>

        <form class="mt-4 space-y-3" @submit.prevent="doEntry">
          <div>
            <label class="text-xs font-semibold text-slate-700">Plate number</label>
            <input
              v-model.trim="entryPlate"
              class="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-mono outline-none ring-sky-200 focus:ring-4"
              placeholder="e.g. AA-12345"
            />
          </div>
          <button class="w-full rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500">
            Allow entry
          </button>
          <div v-if="entryNote" class="rounded-lg bg-amber-50 p-3 text-xs text-amber-800 ring-1 ring-amber-200">
            {{ entryNote }}
          </div>
        </form>
      </section>

      <section class="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
        <div class="text-sm font-bold text-slate-900">Process exit</div>
        <div class="mt-1 text-xs text-slate-500">Search by plate, calculate duration and payment, then confirm.</div>

        <form class="mt-4 space-y-3" @submit.prevent="doExit">
          <div>
            <label class="text-xs font-semibold text-slate-700">Plate number</label>
            <input
              v-model.trim="exitPlate"
              class="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-mono outline-none ring-sky-200 focus:ring-4"
              placeholder="e.g. AA-12345"
            />
          </div>
          <div>
            <label class="text-xs font-semibold text-slate-700">Payment method</label>
            <select v-model="paymentMethod" class="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none ring-sky-200 focus:ring-4">
              <option value="cash">Cash</option>
              <option value="card">Bank Transfer</option>
              <option value="mobile">Mobile Money</option>
            </select>
          </div>
          <button class="w-full rounded-lg bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-sky-500">
            Calculate & complete exit
          </button>
        </form>
      </section>
    </div>

    <section v-if="receipt" class="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
      <div class="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div class="text-xs font-semibold text-slate-600">Receipt</div>
          <div class="mt-1 text-lg font-bold text-slate-900">Session {{ receipt.sessionId }}</div>
        </div>
        <button class="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50" @click="print">
          Print
        </button>
      </div>

      <div class="mt-4 grid gap-3 md:grid-cols-2">
        <div class="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
          <div class="text-xs text-slate-500">Plate</div>
          <div class="mt-1 font-mono text-sm font-semibold text-slate-900">{{ receipt.plateNumber }}</div>
        </div>
        <div class="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
          <div class="text-xs text-slate-500">Type</div>
          <div class="mt-1 text-sm font-semibold text-slate-900">{{ receipt.type }}</div>
        </div>
        <div class="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
          <div class="text-xs text-slate-500">Entry</div>
          <div class="mt-1 text-sm font-semibold text-slate-900">{{ dt(receipt.entryTime) }}</div>
        </div>
        <div class="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
          <div class="text-xs text-slate-500">Exit</div>
          <div class="mt-1 text-sm font-semibold text-slate-900">{{ dt(receipt.exitTime) }}</div>
        </div>
        <div class="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
          <div class="text-xs text-slate-500">Duration</div>
          <div class="mt-1 text-sm font-semibold text-slate-900">{{ receipt.minutes }} min</div>
        </div>
        <div class="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
          <div class="text-xs text-slate-500">Amount</div>
          <div class="mt-1 text-sm font-semibold text-slate-900">{{ fmt(receipt.amount) }} ETB</div>
          <div class="mt-1 text-[11px] text-slate-500">Method: {{ receipt.paymentMethod }}</div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useStore } from "vuex";
import JsBarcode from "jsbarcode";
import { toast } from "@/toast";

const store = useStore();

const entryPlate = ref("");
const exitPlate = ref("");
const paymentMethod = ref("cash");
const entryNote = ref("");
const receipt = ref(null);

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatReceiptDateTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  } catch {
    return String(iso || "");
  }
}

/**
 * 80mm thermal-style entry slip: barcode + human-readable plate and entry time.
 * Uses a hidden iframe so async entry flow is not blocked by popup rules.
 */
function printEntrySlip(session) {
  if (!session?.plateNumber || !session?.entryTime) return;

  const plate = String(session.plateNumber);
  let barcodeDataUrl = "";
  try {
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, plate, {
      format: "CODE128",
      width: 1.85,
      height: 54,
      displayValue: false,
      margin: 6,
      background: "#ffffff",
      lineColor: "#0f172a",
    });
    barcodeDataUrl = canvas.toDataURL("image/png");
  } catch {
    /* plate may use chars unsupported by encoder — text-only slip still prints */
  }

  const entryLine = formatReceiptDateTime(session.entryTime);
  // const typeLabel = session.type === "permanent" ? "Permanent" : "Guest";
  // const refId = escapeHtml(session.id || "");

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<title>Entry — ${escapeHtml(plate)}</title>
<style>
  @page { size: 80mm auto; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: ui-sans-serif, system-ui, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    color: #0f172a;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    width: 72mm;
    max-width: 72mm;
    margin: 0 auto;
  }
  .slip { padding: 4mm 4mm 6mm; text-align: center; }
  .eyebrow { font-size: 8.5pt; font-weight: 700; letter-spacing: 0.22em; color: #64748b; text-transform: uppercase; margin: 0 0 4px; }
  .title { font-size: 13.5pt; font-weight: 800; letter-spacing: 0.12em; margin: 0 0 8px; color: #0f172a; }
  .rule { border: 0; border-top: 1px dashed #94a3b8; margin: 8px 10px; }
  .lbl { font-size: 7.5pt; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: #64748b; margin: 10px 0 4px; }
  .plate { font-size: 20pt; font-weight: 800; font-family: ui-monospace, "Cascadia Mono", Consolas, monospace; letter-spacing: 0.08em; line-height: 1.15; margin: 2px 0 12px; word-break: break-all; }
  .bc { margin: 4px auto 6px; max-width: 100%; }
  .bc img { display: block; margin: 0 auto; max-width: 100%; height: auto; image-rendering: pixelated; image-rendering: crisp-edges; }
  .plate-sub { font-size: 11pt; font-weight: 700; font-family: ui-monospace, monospace; letter-spacing: 0.06em; color: #334155; margin-top: 4px; }
  .dt-block { margin-top: 12px; }
  .dt { font-size: 11.5pt; font-weight: 600; line-height: 1.35; color: #0f172a; }
  .meta { margin-top: 12px; font-size: 8pt; color: #64748b; line-height: 1.5; }
  .footer { margin-top: 12px; font-size: 9pt; font-weight: 600; color: #475569; }
</style>
</head>
<body>
  <div class="slip"> 
    <h1 class="title">ENTRY TICKET</h1>
    <hr class="rule"/>  
    <p class="plate">Plate No: ${escapeHtml(plate)}</p>
    ${
      barcodeDataUrl
        ? `<div class="bc"><img src="${barcodeDataUrl}" width="280" alt=""/></div>`
        : `<p class="plate-sub">${escapeHtml(plate)}</p>`
    }
    <div class="dt-block">
      <p class="lbl">Entry date &amp; time</p>
      <p class="dt">${escapeHtml(entryLine)}</p>
    </div>
    <hr class="rule"/>
    <p class="footer">Valid for active session · Present at exit</p>
  </div>
</body>
</html>`;

  const iframe = document.createElement("iframe");
  iframe.setAttribute("title", "Entry receipt print");
  iframe.setAttribute(
    "style",
    "position:fixed;width:0;height:0;border:0;right:0;bottom:0;opacity:0;pointer-events:none"
  );
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument;
  if (!doc) {
    document.body.removeChild(iframe);
    toast.warning("Could not prepare print layout.");
    return;
  }
  doc.open();
  doc.write(html);
  doc.close();

  const win = iframe.contentWindow;
  const cleanup = () => {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };

  const runPrint = () => {
    try {
      win.focus();
      win.print();
    } finally {
      setTimeout(cleanup, 500);
    }
  };

  if (win?.document?.readyState === "complete") {
    setTimeout(runPrint, 50);
  } else {
    iframe.onload = () => setTimeout(runPrint, 50);
  }
}

async function doEntry() {
  entryNote.value = "";
  receipt.value = null;
  const res = await store.dispatch("registerEntry", { plateNumber: entryPlate.value });
  if (!res?.ok) return toast.error(res?.message || "Entry failed.");
  if (res.warning) {
    entryNote.value = `${res.warning} Treated as guest.`;
    toast.warning(entryNote.value);
  } else {
    toast.success("Entry registered.");
  }
  if (res.session) {
    printEntrySlip(res.session);
  }
  entryPlate.value = "";
}

async function doExit() {
  entryNote.value = "";
  const res = await store.dispatch("processExit", { plateNumber: exitPlate.value, paymentMethod: paymentMethod.value });
  if (!res?.ok) return toast.error(res?.message || "Exit failed.");
  receipt.value = res.receipt;
  toast.success("Exit completed.");
  exitPlate.value = "";
}

function print() {
  window.print();
}

function fmt(v) {
  const n = Number(v) || 0;
  return n.toFixed(2);
}

function dt(iso) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
</script>

